import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { useRef } from 'react';
import { X, Facebook, AlertCircle, CheckCircle2, AlertTriangle, Upload } from 'lucide-react';
import {
    createPageConfig, updatePageConfig, listMetaAccounts, listMetaPages, listAssets,
    uploadAsset, type SocialPageConfig, type SocialAsset,
} from '@/services/api/social-publisher';


// Listas pré-definidas
const TONE_OPTIONS = [
    'descontraído',
    'próximo',
    'motivacional',
    'inspirador',
    'informativo',
    'profissional',
    'divertido',
    'reflexivo',
    'urgente',
    'empático',
];

const HASHTAG_PRESETS = [
    '#dicas', '#motivacao', '#inspiracao', '#vida', '#lifestyle', '#brasil',
    '#mindset', '#sucesso', '#foco', '#conquista', '#amor', '#familia',
    '#marketing', '#empreendedorismo', '#negocios', '#vendas', '#marca', '#estrategia',
    '#saude', '#bemestar', '#alimentacao', '#fitness',
    '#educacao', '#aprendizado', '#conteudo', '#blog',
    '#beleza', '#moda', '#estilo', '#tendencia',
];


function formatTimeMask(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function TimeChips({
    label, value, onChange, color = 'purple',
}: {
    label: string; value: string[];
    onChange: (next: string[]) => void;
    color?: 'purple' | 'fuchsia';
}) {
    const [draft, setDraft] = useState('');
    const valid = /^([01]\d|2[0-3]):[0-5]\d$/.test(draft);
    const add = () => {
        if (!valid || value.includes(draft)) return;
        onChange([...value, draft].sort());
        setDraft('');
    };
    const remove = (t: string) => onChange(value.filter((v) => v !== t));
    const chip = color === 'purple'
        ? 'bg-purple-100 text-purple-700 border-purple-200'
        : 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200';
    return (
        <div>
            <Label className="text-xs text-gray-500">{label}</Label>
            <div className="mt-1 flex flex-wrap gap-1.5 min-h-[24px]">
                {value.length === 0 && (
                    <span className="text-xs text-gray-400">Nenhum horário ainda</span>
                )}
                {value.map((t) => (
                    <span
                        key={t}
                        className={`text-xs px-2 py-0.5 rounded-full border ${chip} inline-flex items-center gap-1`}
                    >
                        {t}
                        <button onClick={() => remove(t)} className="hover:text-red-600">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="mt-2">
                <Input
                    placeholder="ex: 08:00"
                    value={draft}
                    onChange={(e) => setDraft(formatTimeMask(e.target.value))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); add(); }
                    }}
                    onBlur={() => { if (valid) add(); }}
                    inputMode="numeric"
                    maxLength={5}
                    className="h-8 text-sm"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                    Digite os números e Enter (ou clique fora) pra adicionar.
                </p>
            </div>
        </div>
    );
}


function AssetSelect({
    label, value, onChange, kind, helperText,
}: {
    label: string;
    value: string;
    onChange: (id: string) => void;
    kind: 'logo' | 'model_photo';
    helperText?: string;
}) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const fileRef = useRef<HTMLInputElement>(null);
    const { data: assets = [] } = useQuery({
        queryKey: ['social-publisher', 'assets', kind],
        queryFn: () => listAssets({ kind }),
    });
    const current = assets.find((a: SocialAsset) => String(a.id) === value);

    const up = useMutation({
        mutationFn: (file: File) => uploadAsset(file, kind),
        onSuccess: (asset) => {
            toast({ title: 'Upload OK' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'assets'] });
            // Seleciona automaticamente o asset recém-criado
            onChange(String(asset.id));
            if (fileRef.current) fileRef.current.value = '';
        },
        onError: (e: any) => {
            toast({
                title: 'Erro upload',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    return (
        <div>
            <Label className="text-xs text-gray-500">{label}</Label>
            <div className="flex gap-2">
                <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="(nenhum)" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">(nenhum)</SelectItem>
                        {assets.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                                {a.file_path.split('/').pop()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) up.mutate(f);
                    }}
                    className="hidden"
                />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={up.isPending}
                    title="Fazer upload"
                    className="px-3"
                >
                    <Upload className="w-3.5 h-3.5" />
                </Button>
            </div>
            {current && (
                <img
                    src={current.file_url}
                    alt={label}
                    className="mt-2 max-h-16 rounded border"
                />
            )}
            {helperText && <p className="text-[11px] text-gray-400 mt-1">{helperText}</p>}
        </div>
    );
}


interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editing: SocialPageConfig | null;
    onSaved: () => void;
}

export default function PageConfigForm({ open, onOpenChange, editing, onSaved }: Props) {
    const { toast } = useToast();
    const [accountId, setAccountId] = useState<string>('');
    const [pagePk, setPagePk] = useState<string>('');
    const [language, setLanguage] = useState('pt-BR');
    const [tone, setTone] = useState('');
    const [hashtags, setHashtags] = useState<string[]>([]);
    const [outfitStyle, setOutfitStyle] = useState('');
    const [logoId, setLogoId] = useState<string>('');
    const [photoId, setPhotoId] = useState<string>('');
    const [storyTimes, setStoryTimes] = useState<string[]>([]);
    const [postTimes, setPostTimes] = useState<string[]>([]);
    const [horizon, setHorizon] = useState('7');
    const [maxPerDay, setMaxPerDay] = useState('5');
    const [status, setStatus] = useState('active');

    const { data: rawAccounts = [], isLoading: loadingAccounts } = useQuery({
        queryKey: ['social-publisher', 'meta-accounts'],
        queryFn: listMetaAccounts,
        enabled: open,
    });
    // Agrupa contas pelo nome (ignora re-conexões duplicadas em apps distintos).
    // Resultado: 1 entrada por nome, com lista interna de account_ids agregados
    // e pages_count somado. A "key" do select é o NOME (lowercase normalizado).
    const groupedAccounts = (() => {
        const map = new Map<string, {
            key: string;
            name: string;
            picture_url: string | null;
            pages_count: number;
            account_ids: number[];
        }>();
        for (const a of rawAccounts) {
            const name = (a.meta_user_name || '').trim();
            const key = name.toLowerCase() || `id:${a.id}`;
            const existing = map.get(key);
            if (existing) {
                existing.pages_count += a.pages_count || 0;
                existing.account_ids.push(a.id);
                if (!existing.picture_url && a.picture_url) existing.picture_url = a.picture_url;
            } else {
                map.set(key, {
                    key,
                    name: name || `Conta #${a.id}`,
                    picture_url: a.picture_url || null,
                    pages_count: a.pages_count || 0,
                    account_ids: [a.id],
                });
            }
        }
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    })();

    // Lista TODAS as páginas do user uma vez; filtra no client pelas account_ids
    // do grupo selecionado. Evita chamar /meta-pages?account_id=X várias vezes
    // (uma por row de oauth_accounts) só pra fazer union no client depois.
    const { data: allPages = [], isLoading: loadingPages } = useQuery({
        queryKey: ['social-publisher', 'meta-pages-all'],
        queryFn: () => listMetaPages(),
        enabled: open,
    });
    const currentGroup = groupedAccounts.find((g) => g.key === accountId);
    const metaPages = currentGroup
        ? allPages.filter((p) => p.account_id != null && currentGroup.account_ids.includes(p.account_id))
        : [];

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setPagePk(String(editing.page_pk));
            const pj: any = editing.persona_json || {};
            setLanguage(pj.language || 'pt-BR');
            setTone(pj.tone || '');
            setHashtags(
                Array.isArray(pj.hashtags)
                    ? pj.hashtags
                    : (typeof pj.hashtags === 'string' ? pj.hashtags.split(/\s+/).filter(Boolean) : []),
            );
            setOutfitStyle(pj.outfit_style || '');
            setLogoId(editing.logo_asset_id ? String(editing.logo_asset_id) : '');
            setPhotoId(editing.model_photo_asset_id ? String(editing.model_photo_asset_id) : '');
            setStoryTimes(editing.story_schedule_times || []);
            setPostTimes(editing.post_schedule_times || []);
            setHorizon(String(editing.horizon_days || 7));
            setMaxPerDay(String(editing.max_per_day || 5));
            setStatus(editing.status || 'active');
        } else {
            setAccountId('');
            setPagePk('');
            setLanguage('pt-BR');
            setTone(''); setHashtags([]); setOutfitStyle('');
            setLogoId(''); setPhotoId('');
            setStoryTimes(['08:00', '12:00', '22:00']);
            setPostTimes(['12:00', '20:00']);
            setHorizon('7'); setMaxPerDay('5');
            setStatus('active');
        }
    }, [open, editing]);

    // Em edição: pré-seleciona o GRUPO da conta da página (matching pelo account_id
    // → encontra qual grupo contém esse account_id → seta a key do grupo).
    useEffect(() => {
        if (!editing || !open || accountId) return;
        const p = allPages.find((x) => x.id === editing.page_pk);
        if (!p?.account_id) return;
        const g = groupedAccounts.find((gg) => gg.account_ids.includes(p.account_id!));
        if (g) setAccountId(g.key);
    }, [editing, open, allPages, groupedAccounts, accountId]);

    const mutation = useMutation({
        mutationFn: async () => {
            const persona_json = {
                language,
                tone: tone || undefined,
                hashtags,
                outfit_style: outfitStyle.trim() || undefined,
            };
            // Persona name = nome da página (sempre).
            const personaName = selectedPage?.page_name
                || editing?.persona_name
                || null;
            const payload = {
                page_pk: Number(pagePk),
                persona_name: personaName,
                persona_json,
                logo_asset_id: logoId ? Number(logoId) : null,
                model_photo_asset_id: photoId ? Number(photoId) : null,
                story_schedule_times: storyTimes,
                post_schedule_times: postTimes,
                horizon_days: Number(horizon),
                max_per_day: Number(maxPerDay),
                status,
            };
            return editing
                ? updatePageConfig(editing.id, payload)
                : createPageConfig(payload);
        },
        onSuccess: () => {
            toast({ title: editing ? 'Atualizada' : 'Criada' });
            onSaved();
        },
        onError: (e: any) => {
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha ao salvar',
                variant: 'destructive',
            });
        },
    });

    const canSave = !!pagePk && (storyTimes.length + postTimes.length > 0);
    const selectedAccount = currentGroup;
    const selectedPage = metaPages.find((p) => String(p.id) === pagePk)
        || allPages.find((p) => String(p.id) === pagePk);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden border-none rounded-2xl">
                <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="text-base font-semibold text-gray-800">
                            {editing ? 'Editar configuração' : 'Configurar página'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Persona, horários e visuais da página
                        </p>
                    </div>

                    <Tabs defaultValue="page" className="px-6 pt-3">
                        <TabsList>
                            <TabsTrigger value="page">Página & persona</TabsTrigger>
                            <TabsTrigger value="schedule">Horários</TabsTrigger>
                        </TabsList>

                        <div className="py-4 max-h-[60vh] overflow-y-auto">
                            <TabsContent value="page" className="space-y-4 mt-0">
                                {groupedAccounts.length === 0 && !loadingAccounts ? (
                                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                        <span>
                                            Nenhuma conta Meta conectada. Conecte uma em{' '}
                                            <Link to="/meta/connection" className="underline">/meta/connection</Link>.
                                        </span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-gray-500">Conta Meta</Label>
                                            <Select
                                                value={accountId}
                                                onValueChange={(v) => {
                                                    setAccountId(v);
                                                    if (!editing) setPagePk('');
                                                }}
                                                disabled={!!editing}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder={
                                                        loadingAccounts ? 'Carregando...' : 'Selecione a conta'
                                                    }>
                                                        {selectedAccount && (
                                                            <span className="flex items-center gap-2">
                                                                {selectedAccount.picture_url ? (
                                                                    <img
                                                                        src={selectedAccount.picture_url}
                                                                        className="w-5 h-5 rounded-full"
                                                                        alt=""
                                                                    />
                                                                ) : (
                                                                    <Facebook className="w-4 h-4 text-gray-300" />
                                                                )}
                                                                <span className="truncate">{selectedAccount.name}</span>
                                                            </span>
                                                        )}
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {groupedAccounts.map((g) => (
                                                        <SelectItem key={g.key} value={g.key}>
                                                            <span className="flex items-center gap-2">
                                                                {g.picture_url ? (
                                                                    <img src={g.picture_url} className="w-5 h-5 rounded-full" alt="" />
                                                                ) : (
                                                                    <Facebook className="w-4 h-4 text-gray-300" />
                                                                )}
                                                                <span className="truncate">{g.name}</span>
                                                                <span className="text-[10px] text-gray-400 ml-1">
                                                                    {g.pages_count} pág.
                                                                </span>
                                                            </span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-gray-500">
                                                Página
                                                {accountId && metaPages.length > 0 && (
                                                    <span className="text-[10px] text-gray-400 ml-1">
                                                        ({metaPages.length} disponíveis)
                                                    </span>
                                                )}
                                            </Label>
                                            <SearchableSelect
                                                value={pagePk}
                                                onValueChange={setPagePk}
                                                placeholder={
                                                    !accountId
                                                        ? 'Selecione a conta primeiro'
                                                        : loadingPages
                                                            ? 'Carregando páginas...'
                                                            : 'Selecione uma página'
                                                }
                                                searchPlaceholder="Buscar página..."
                                                options={metaPages.map((p) => ({
                                                    value: String(p.id),
                                                    label: `${p.page_name}${p.has_valid_token ? '' : ' ⚠ token'}`,
                                                }))}
                                            />
                                        </div>
                                    </div>
                                )}

                                {editing && (
                                    <div>
                                        <Label className="text-xs text-gray-500">Status</Label>
                                        <div className="flex gap-2 mt-1">
                                            {([
                                                { v: 'active', label: 'Ativa', on: 'bg-emerald-600 border-emerald-600 text-white', off: 'text-emerald-700 border-emerald-200 hover:bg-emerald-50' },
                                                { v: 'paused', label: 'Pausada', on: 'bg-amber-500 border-amber-500 text-white', off: 'text-amber-700 border-amber-200 hover:bg-amber-50' },
                                                { v: 'archived', label: 'Arquivada', on: 'bg-gray-600 border-gray-600 text-white', off: 'text-gray-600 border-gray-200 hover:bg-gray-50' },
                                            ] as const).map((o) => (
                                                <button
                                                    key={o.v}
                                                    type="button"
                                                    onClick={() => setStatus(o.v)}
                                                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${status === o.v ? o.on : `bg-white ${o.off}`}`}
                                                >
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            Pausada/Arquivada: esta página para de gerar, agendar e publicar. Não apaga o que já existe.
                                        </p>
                                    </div>
                                )}

                                {selectedPage && (
                                    <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl p-3">
                                        {selectedPage.picture_url ? (
                                            <img
                                                src={selectedPage.picture_url}
                                                className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                                                alt=""
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                                                <Facebook className="w-5 h-5" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-800 truncate">
                                                {selectedPage.page_name}
                                            </div>
                                            <div className="text-[11px] text-gray-500 truncate">
                                                ID: {selectedPage.page_id}
                                            </div>
                                        </div>
                                        {selectedPage.has_valid_token ? (
                                            <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> token ok
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                                                <AlertTriangle className="w-3.5 h-3.5" /> token expirado
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="pt-2 border-t">
                                    <div className="text-xs font-medium text-gray-700 mb-2">
                                        🎭 Persona desta página
                                        {selectedPage?.page_name && (
                                            <span className="text-gray-400 ml-1 font-normal">
                                                · usa o nome <em>{selectedPage.page_name}</em>
                                            </span>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-gray-500">Idioma</Label>
                                            <Select value={language} onValueChange={setLanguage}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="pt-BR">Português (BR)</SelectItem>
                                                    <SelectItem value="en-US">English (US)</SelectItem>
                                                    <SelectItem value="es-ES">Español</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-gray-500">Tom</Label>
                                            <Select value={tone || 'none'} onValueChange={(v) => setTone(v === 'none' ? '' : v)}>
                                                <SelectTrigger><SelectValue placeholder="(sem tom específico)" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">(sem tom específico)</SelectItem>
                                                    {TONE_OPTIONS.map((t) => (
                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <Label className="text-xs text-gray-500">
                                            Vestimenta / estilo visual (opcional)
                                        </Label>
                                        <Input
                                            placeholder='ex: "roupa sensual, lingerie ou biquíni"'
                                            value={outfitStyle}
                                            onChange={(e) => setOutfitStyle(e.target.value)}
                                        />
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            Aplicado a todas as imagens geradas dessa page.
                                        </p>
                                    </div>

                                    <div className="mt-3">
                                        <Label className="text-xs text-gray-500">
                                            Hashtags padrão{' '}
                                            <span className="text-gray-400">({hashtags.length} selecionadas)</span>
                                        </Label>
                                        <div className="mt-1 flex flex-wrap gap-1.5 p-3 bg-gray-50 rounded-lg border">
                                            {HASHTAG_PRESETS.map((h) => {
                                                const active = hashtags.includes(h);
                                                return (
                                                    <button
                                                        key={h}
                                                        type="button"
                                                        onClick={() => {
                                                            setHashtags((prev) =>
                                                                prev.includes(h)
                                                                    ? prev.filter((x) => x !== h)
                                                                    : [...prev, h],
                                                            );
                                                        }}
                                                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                                            active
                                                                ? 'bg-purple-600 text-white border-purple-600'
                                                                : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
                                                        }`}
                                                    >
                                                        {h}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        <AssetSelect
                                            label="Logo (opcional)"
                                            value={logoId}
                                            onChange={setLogoId}
                                            kind="logo"
                                            helperText="Clique no botão pra fazer upload direto"
                                        />
                                        <AssetSelect
                                            label="Foto da pessoa-marca"
                                            value={photoId}
                                            onChange={setPhotoId}
                                            kind="model_photo"
                                            helperText={
                                                !photoId
                                                    ? 'Sem foto → fallback usa a foto da página'
                                                    : undefined
                                            }
                                        />
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="schedule" className="space-y-4 mt-0">
                                <div className="grid grid-cols-2 gap-4">
                                    <TimeChips
                                        label="Horários de POST (feed)"
                                        value={postTimes}
                                        onChange={setPostTimes}
                                        color="purple"
                                    />
                                    <TimeChips
                                        label="Horários de STORY"
                                        value={storyTimes}
                                        onChange={setStoryTimes}
                                        color="fuchsia"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-gray-500">Horizonte (dias)</Label>
                                        <Input type="number" value={horizon} onChange={(e) => setHorizon(e.target.value)} />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-gray-500">Max/dia (total)</Label>
                                        <Input type="number" value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} />
                                    </div>
                                </div>
                            </TabsContent>
                        </div>
                    </Tabs>

                    <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50/50">
                        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => mutation.mutate()}
                            disabled={!canSave || mutation.isPending}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {mutation.isPending ? 'Salvando...' : (editing ? 'Atualizar' : 'Criar')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
